import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StatusPill from 'features/IRWorkstation/components/StatusPill';

const colorMap = {
	red: 'score.down',
	green: 'score.up',
	amber: 'score.warn',
	vdr: 'vdr.text',
};

const FieldRow = ({ label, value, valueColor, pill }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			py: '3px',
		}}
	>
		<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
			{label}
		</Typography>
		{pill ? (
			<StatusPill label={value} color={pill.color} />
		) : (
			<Typography
				sx={{
					fontSize: '12px',
					fontWeight: 700,
					color: valueColor
						? colorMap[valueColor] || 'text.primary'
						: 'text.primary',
					textAlign: 'right',
					maxWidth: '60%',
				}}
			>
				{value}
			</Typography>
		)}
	</Box>
);

FieldRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
	valueColor: PropTypes.string,
	pill: PropTypes.shape({
		label: PropTypes.string,
		color: PropTypes.string,
	}),
};

const ActionDetails = ({ title, description }) => (
	<Box
		sx={{
			backgroundColor: 'vdr.bg',
			border: '0.5px solid',
			borderColor: 'vdr.border',
			borderRadius: '8px',
			padding: '12px 14px',
			mt: 1,
		}}
	>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'vdr.text',
				mb: '4px',
			}}
		>
			↔ {title}
		</Typography>

		<Typography sx={{ fontSize: '11px', color: 'vdr.text' }}>
			{description}
		</Typography>
	</Box>
);

ActionDetails.propTypes = {
	block: PropTypes.shape({
		title: PropTypes.string.isRequired,
		description: PropTypes.string.isRequired,
	}).isRequired,
};


const SectionLabel = ({ text }) => (
	<Typography
		sx={{
			fontSize: '11px',
			fontWeight: 600,
			textTransform: 'uppercase',
			color: 'text.secondary',
			mb: '6px',
		}}
	>
		{text}
	</Typography>
);

SectionLabel.propTypes = {
	text: PropTypes.string.isRequired,
};

const DetailSection = ({ event_context }) => {
	const labelMap = {
		lp_title: 'LP',
		indication: 'Indication',
		conditions: 'Conditions',
		pipeline_state: 'Pipeline state',
		sessions_today: 'Sessions today',
		total_time: 'Total time',
		sections_accessed: 'Sections accessed',
		vs_cohort_average: 'vs. cohort average',
	};

	return (
		<Box>
			<SectionLabel text={'Details'} />

			<Box
				sx={{
					backgroundColor: 'background.subtle',
					borderRadius: '8px',
					padding: '8px 10px',
				}}
			>
				{Object.entries(event_context).map(([key, value]) => (
					<FieldRow
						key={key}
						label={
							labelMap[key] ||
							key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
						}
						value={value}
					/>
				))}
			</Box>
		</Box>
	);
};

DetailSection.propTypes = {
	event_context: PropTypes.object.isRequired,
};

const AlertsDrawer = ({ alert, open, onClose }) => {
	const creationDatetime = new Date(
		alert?.event_time || Date.now()
	).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});

	return (
		<>
			{/* backdrop */}
			<Box
				data-testid='alert-backdrop'
				onClick={onClose}
				sx={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0,0,0,0.25)',
					opacity: open ? 1 : 0,
					pointerEvents: open ? 'auto' : 'none',
					transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)',
					zIndex: 1200,
				}}
			/>
			{/* panel */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					right: 0,
					bottom: 0,
					width: 520,
					backgroundColor: 'background.paper',
					transform: open ? 'translateX(0)' : 'translateX(100%)',
					transition: 'transform 225ms cubic-bezier(0, 0, 0.2, 1)',
					zIndex: 1201,
					display: 'flex',
					flexDirection: 'column',
					boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						padding: '16px 18px 14px',
						borderBottom: '0.5px solid',
						borderColor: 'border.light',
						flexShrink: 0,
					}}
				>
					<Box>
						<Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
							{`${alert.event?.event_title}`}
						</Typography>

						<Typography
							sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px' }}
						>
							{`${creationDatetime} ${creationDatetime && alert.event?.event_subtitle ? ' · ' : ''} ${alert.event?.event_subtitle}`}
						</Typography>
					</Box>

					<IconButton
						data-testid='alert-drawer-close-button'
						size='small'
						onClick={onClose}
						sx={{
							width: 28,
							height: 28,
							borderRadius: '6px',
							border: '0.5px solid',
							borderColor: 'border.light',
							color: 'text.secondary',
							flexShrink: 0,
							'&:hover': { backgroundColor: 'background.subtle' },
						}}
					>
						<CloseIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Box>

				{/* Body */}
				<Box
					sx={{
						padding: '16px 18px',
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					{alert.event?.event_detail && <Box>{alert.event?.event_detail}</Box>}

					{/* Details */}
					{alert.event?.event_context &&
						Object.keys(alert.event.event_context).length > 0 && (
							<DetailSection
								event_detail={alert.event.event_detail}
								event_context={alert.event.event_context}
							/>
						)}

					{/* Action details */}
					{alert.action?.action_title && alert.action?.action_description && (
						<ActionDetails
							title={alert.action.action_title}
							description={alert.action.action_description}
						/>
					)}

					{/* [button] action_description */}
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							flexWrap: 'wrap',
						}}
					>
						{/* TODO: Button is hardcoded to "Open in Raise VDR" now */}
						<Button
							key={`alert-action-vdr-button`}
							href={`#${alert.action?.action_link}`}
							variant='outlined'
							size='small'
							sx={{
								fontSize: '12px',
								fontWeight: 500,
								padding: '7px 14px',
								borderRadius: '6px',
								backgroundColor: 'vdr.bg',
								color: 'vdr.text',
								borderColor: 'vdr.border',
								cursor: 'pointer',
								'&:hover': { backgroundColor: 'vdr.hover' },
							}}
						>
							{`↔ Open in Raise VDR`}
						</Button>

						<Button
							key={'alert-action-acknowledge-button'}
							variant='outlined'
							size='small'
							sx={{
								fontSize: '12px',
								fontWeight: 500,
								padding: '7px 14px',
								borderRadius: '6px',
								borderColor: 'border.main',
								color: 'text.secondary',
								cursor: 'pointer',
							}}
						>
							{'Acknowledge'}
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	);
};

AlertsDrawer.propTypes = {
	alert: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default AlertsDrawer;
